// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import { compose } from '../../../node_module_clones/recompose';

// Utils
import measurementsConnect from '../measurementsStore/measurementsConnect';
import getTextSelectionCaretRect from './domUtils/getTextSelectionCaretRect';
import animateScrollIntoView from '../../utils/animation/animateScrollIntoView';
import { isPlatformLegacyMobileApp } from '../../platform/utils/platformDetailsUtils';
import { shouldScroll, translateDOMRectIntoScrollableParentCoordinates } from '../../utils/dom/scrollableParentUtils';
import findScrollableDomParent from '../../utils/dom/findScrollableDomParent';
import { getElementId, getShowCaption, getCreatedTime } from '../../../common/elements/utils/elementPropertyUtils';
import {
    isLine,
    isCard,
    isLink,
    isTaskList,
    isTask,
    isCommentThread,
    isTable,
} from '../../../common/elements/utils/elementTypeUtils';
import editorStoreConnect from './store/components/editorStoreConnect';
import usePrevious from '../../utils/react/usePrevious';
import { getTimestamp, TIMES } from '../../../common/utils/timeUtil';
import getParentScrollableSectionForElementId from '../../utils/dom/getParentScrollableSectionForElementId';
import { addMargins } from '../../../common/maths/geometry/rect';

// Hooks
import { useTiptapActiveEditorSelector } from '../tiptapEditor/store/tiptapEditorStoreHooks';

// Selectors
import {
    getCurrentlyEditingElement,
    getCurrentlyEditingEditorId,
} from '../../element/selection/currentlyEditingSelector';
import { getCurrentModalElementId } from '../../element/modal/elementModalCommonSelector';
import { getEditorState } from './store/reducers/editorStoreSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Constants
import { MilanoteApplicationMode } from '../../../common/platform/platformTypes';
import { EMPTY_NODE_SIZE } from '../../../common/tiptap/utils/isTiptapEditorEmpty';

const NO_HEIGHT = { height: 0 };
const IPAD_VIRTUAL_KEYBOARD_TOOLBAR_HEIGHT = 56;

// The margin to add around the selection rectangle when determining whether to scroll and where to scroll to
const SELECTION_RECT_MARGIN = {
    top: 50,
    left: 20,
    bottom: 50,
    right: 20,
};

const isNote = (elementType) =>
    isCard(elementType) ||
    isLink(elementType) ||
    isTaskList(elementType) ||
    isTask(elementType) ||
    isCommentThread(elementType);

const getCurrentlyEditingId = createSelector(
    getCurrentlyEditingElement,
    getCurrentModalElementId,
    (currentlyEditingElement, modalElementId) =>
        !isLine(currentlyEditingElement) && !modalElementId ? getElementId(currentlyEditingElement) : undefined,
);

const mapStateToProps = createStructuredSelector({
    platformDetails: getPlatformDetailsSelector,
    currentlyEditingElement: getCurrentlyEditingElement,
    currentlyEditingId: getCurrentlyEditingId,
    currentlyEditingEditorId: getCurrentlyEditingEditorId,
});

const mapMeasurementsStateToProps = (state, ownProps) => {
    if (!ownProps.currentlyEditingId) return NO_HEIGHT;
    return {
        height: state.getIn(['elementMeasurements', ownProps.currentlyEditingId, 'height']),
    };
};

const mapEditorStateToProps = (state) => {
    const editorState = getEditorState(state);

    if (!editorState) return { draftContentSize: 0 };

    const draftContent = editorState.getCurrentContent();

    if (!draftContent.hasText()) return { draftContentSize: 0 };

    const draftText = draftContent.getPlainText();

    return { draftContentSize: draftText.length };
};

const enhance = compose(
    connect(mapStateToProps),
    measurementsConnect(mapMeasurementsStateToProps),
    editorStoreConnect(mapEditorStateToProps),
);

const ScrollEditedElementIntoView = (props) => {
    const {
        currentlyEditingId,
        height,
        currentlyEditingElement,
        draftContentSize,
        currentlyEditingEditorId,
        platformDetails,
    } = props;

    const elementIsNote = isNote(currentlyEditingElement) || getShowCaption(currentlyEditingElement);

    const tiptapContentSize = useTiptapActiveEditorSelector(
        (editor) => editor?.state.doc.nodeSize - EMPTY_NODE_SIZE || 0,
    );

    const editorContentSize = tiptapContentSize || draftContentSize;

    const previousEditorContentSize = usePrevious(editorContentSize);
    const previousEditorId = usePrevious(currentlyEditingEditorId);

    const isSameEditor = previousEditorId === currentlyEditingEditorId;
    const isNewlyCreated = (getTimestamp() - getCreatedTime(currentlyEditingElement)) / TIMES.SECOND < 1;

    React.useEffect(() => {
        // For now - ignore this scrolling as "useScrollToCaretOnEdit" will handle this
        if (platformDetails.appMode === MilanoteApplicationMode.mobile) return;

        if (!currentlyEditingId || !editorContentSize || isTable(currentlyEditingElement)) return;

        const hasContent = isSameEditor && editorContentSize > 0;
        const hasTextChanged = isSameEditor && previousEditorContentSize !== editorContentSize;

        // Need to save the parent's scroll left, as the automatic scrolling when editing
        // will ruin the animation
        const parent = getParentScrollableSectionForElementId(currentlyEditingId);
        const originalScrollLeft = parent ? parent.scrollLeft : null;

        requestAnimationFrame(() => {
            if (!height || !currentlyEditingId) return;

            // Need to restore the parent's scroll left, as the automatic scrolling when editing
            // will ruin the animation
            if (originalScrollLeft !== null) {
                parent.scrollLeft = originalScrollLeft;
            }

            const selectionLineRect = getTextSelectionCaretRect();

            if (!selectionLineRect || !selectionLineRect.height) return;

            const selection = window.getSelection();
            const scrollableParent =
                parent && parent.contains(selection.focusNode) ? parent : findScrollableDomParent(selection.focusNode);

            if (!scrollableParent || !scrollableParent.contains(selection.focusNode)) return;

            const selectionRectWithMargins = addMargins(SELECTION_RECT_MARGIN, selectionLineRect);
            const targetRect = translateDOMRectIntoScrollableParentCoordinates(
                scrollableParent,
                selectionRectWithMargins,
            );

            const viewportInsetMargins = isPlatformLegacyMobileApp(platformDetails)
                ? { bottom: IPAD_VIRTUAL_KEYBOARD_TOOLBAR_HEIGHT }
                : null;
            const viewportOptions = { insetMargins: viewportInsetMargins };

            const shouldScrollEditedElement = shouldScroll(scrollableParent, targetRect, viewportOptions);
            const isNoteAndShouldScroll = elementIsNote && shouldScrollEditedElement;

            const shouldScrollEditedElementIntoView = isPlatformLegacyMobileApp(platformDetails)
                ? () => shouldScrollEditedElement
                : () => {
                      // this is for making sure it works well when switching between elements
                      // and when its a new card and is dragged to edge of screen
                      if (isNoteAndShouldScroll && !isSameEditor && !isNewlyCreated) return false;

                      // when its a old card and user is just selecting it without typing
                      if (isNoteAndShouldScroll && hasContent && !hasTextChanged) return false;

                      return shouldScrollEditedElement;
                  };

            if (shouldScrollEditedElementIntoView()) {
                animateScrollIntoView({
                    scrollableParent: scrollableParent,
                    targetRect,
                    viewportOptions,
                    interpolationFactor: 0.2,
                });
            }
        });
    }, [height, editorContentSize]);

    return null;
};

ScrollEditedElementIntoView.propTypes = {
    currentlyEditingId: PropTypes.string,
    height: PropTypes.number,
    currentlyEditingElement: PropTypes.object,
    draftContentSize: PropTypes.number,
    currentlyEditingEditorId: PropTypes.string,
    platformDetails: PropTypes.object,
};

export default enhance(ScrollEditedElementIntoView);
